import useGetUserNotifications from "api/Notifications/useGetUserNotifications";
import useMarkAllNotificationsAsRead from "api/Notifications/useMarkAllNotificationsAsRead";
import { useState, useRef, useEffect } from "react";
import { FaRegBell } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import NotificationItem from "./NotificationItem";

/**
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @returns {JSX.Element}
 */
const NotificationBell = (props) => {
	const { isAuthenticated = false } = props;
	const [show, setShow] = useState(false);
	const history = useHistory();

	const dropdownRef = useRef(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		if (!show) return;
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setShow(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [show]);

	const handleViewAll = () => {
		setShow(false);
		history.push("/notifications/all");
	};

	const { data: notifications, isLoading } = useGetUserNotifications({
		recent: true,
		status: "unread",
	});

	const { mutateAsync: markAllAsRead, isLoading: isMarkingAll } = useMarkAllNotificationsAsRead();

	const unreadNotifications = notifications?.data?.records || [];
	const unreadCount = Number(notifications?.data?.unread_count ?? unreadNotifications.length);

	if (!isAuthenticated) return null;

	return (
		<div className="relative inline-block" ref={dropdownRef}>
			<button
				className="relative p-2 rounded-full hover:bg-gray-100 focus:outline-none"
				onClick={() => setShow((s) => !s)}
				aria-label="Notifications"
			>
				<FaRegBell size={24} />
				{unreadCount > 0 &&
					(isLoading ? (
						<div className="w-4 h-4 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin absolute -top-1 -right-1" />
					) : (
						<span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
							{unreadCount}
						</span>
					))}
			</button>

			{show && (
				<div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
					<div className="flex justify-between items-center px-4 py-2 font-bold border-b border-gray-200">
						<div>
							<p className="text-base">My notifications</p>
							<p className="text-xs text-gray-500 font-normal">{unreadCount} unread</p>
						</div>

						<button
							className="p-1 rounded-full hover:bg-gray-200 focus:outline-none"
							onClick={() => setShow(false)}
							aria-label="Close notifications dropdown"
						>
							<FiX size={20} />
						</button>
					</div>

					<div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
						<button
							type="button"
							onClick={() => markAllAsRead()}
							disabled={unreadCount === 0 || isMarkingAll}
							className="text-sm text-akgreen hover:underline disabled:text-gray-400 disabled:no-underline"
						>
							Mark all as read
						</button>
						<button
							type="button"
							className="text-blue-600 hover:underline text-sm font-medium"
							onClick={handleViewAll}
						>
							View all
						</button>
					</div>

					<div className="max-h-80 overflow-y-auto">
						{unreadNotifications.length === 0 ? (
							<div className="px-4 py-3 text-gray-500 text-sm">No unread notifications</div>
						) : (
							unreadNotifications.map((notif) => (
								<NotificationItem key={notif.id} {...notif} onClose={() => setShow(false)} />
							))
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default NotificationBell;
